import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Grid3X3, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { slides } from '@/config/slides';
import ExportMenu from '@/components/ExportMenu';
import { exportToPdf, exportToPptx, exportToZip, exportSingleToPng } from '@/lib/exportUtils';

const BASE_WIDTH = 1920;
const BASE_HEIGHT = 1080;

// Context for responsive breakpoints
export const ResponsiveContext = createContext({
  isMobile: false,
  isTablet: false,
  isDesktop: true,
  screenWidth: 1920,
  screenHeight: 1080,
});

export const useResponsive = () => useContext(ResponsiveContext);

const ScalableSlide = ({ children, isFullscreen }) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    scale: 1,
    width: BASE_WIDTH,
    height: BASE_HEIGHT,
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;

      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const isMobile = screenWidth < 768;
      const isTablet = screenWidth >= 768 && screenWidth < 1024;
      const isDesktop = screenWidth >= 1024;

      const parentW = isFullscreen ? screenWidth : containerRef.current.parentElement?.clientWidth || 0;
      const parentH = isFullscreen ? screenHeight : containerRef.current.parentElement?.clientHeight || 0;

      if (!parentW || !parentH) return;

      // For mobile and tablet, use native responsive layout (no scaling)
      if (isMobile || isTablet) {
        setDimensions({
          scale: 1,
          width: parentW,
          height: isFullscreen ? parentH : 'auto',
          useNativeLayout: true,
          isMobile,
          isTablet,
          isDesktop,
          screenWidth,
          screenHeight,
        });
      } else if (isFullscreen) {
        // Desktop fullscreen - scale to fill screen height
        const scaleX = parentW / BASE_WIDTH;
        const scaleY = parentH / BASE_HEIGHT;
        const scale = Math.min(scaleX, scaleY); // Use the smaller scale to fit both dimensions

        setDimensions({
          scale,
          width: BASE_WIDTH,
          height: BASE_HEIGHT,
          useNativeLayout: false,
          isMobile,
          isTablet,
          isDesktop,
          screenWidth,
          screenHeight,
        });
      } else {
        // Desktop non-fullscreen - scale to container
        const targetWidth = Math.min(parentW, 1400);
        const newScale = targetWidth / BASE_WIDTH;
        setDimensions({
          scale: newScale,
          width: BASE_WIDTH,
          height: BASE_HEIGHT,
          useNativeLayout: false,
          isMobile,
          isTablet,
          isDesktop,
          screenWidth,
          screenHeight,
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFullscreen]);

  const responsiveValue = {
    isMobile: dimensions.isMobile,
    isTablet: dimensions.isTablet,
    isDesktop: dimensions.isDesktop,
    screenWidth: dimensions.screenWidth,
    screenHeight: dimensions.screenHeight,
  };

  // Native responsive layout for mobile/tablet
  if (dimensions.useNativeLayout) {
    return (
      <ResponsiveContext.Provider value={responsiveValue}>
        <div
          ref={containerRef}
          className={`relative w-full ${isFullscreen ? 'h-full' : 'min-h-[100svh]'} bg-background overflow-y-auto overflow-x-hidden`}
        >
          <div className={`w-full ${isFullscreen ? 'min-h-full' : ''} ${!isFullscreen ? 'border border-border rounded-2xl' : ''}`}>
            {children}
          </div>
        </div>
      </ResponsiveContext.Provider>
    );
  }

  // Desktop scaled layout
  return (
    <ResponsiveContext.Provider value={responsiveValue}>
      <div
        ref={containerRef}
        className={`relative flex ${isFullscreen ? 'items-start justify-center' : 'items-center justify-center'} w-full ${isFullscreen ? 'h-full bg-background' : ''}`}
        style={{
          height: isFullscreen ? '100%' : `${BASE_HEIGHT * dimensions.scale}px`
        }}
      >
        <div
          style={{
            width: dimensions.width,
            height: dimensions.height,
            transform: `scale(${dimensions.scale})`,
            transformOrigin: isFullscreen ? 'top center' : 'center center',
          }}
          className={`overflow-hidden ${isFullscreen ? '' : 'bg-card shadow-card border border-border rounded-2xl'}`}
        >
          {children}
        </div>
      </div>
    </ResponsiveContext.Provider>
  );
};

const PitchDeck = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isGridView, setIsGridView] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [exportConfig, setExportConfig] = useState(null); // { scope: 'all'|'current', format: 'pdf'|'pptx'|'zip'|'png' }
  const exportContainerRef = useRef(null);

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < slides.length) {
      setCurrentSlide(index);
      setIsGridView(false);
    }
  }, []);

  const nextSlide = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  }, [currentSlide]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  }, [currentSlide]);

  const toggleFullscreen = () => {
    const doc = window.document;
    const docEl = doc.documentElement;

    const requestFullScreen = docEl.requestFullscreen || docEl.mozRequestFullScreen || docEl.webkitRequestFullScreen || docEl.msRequestFullscreen;
    const cancelFullScreen = doc.exitFullscreen || doc.mozCancelFullScreen || doc.webkitExitFullscreen || doc.msExitFullscreen;

    if (!doc.fullscreenElement && !doc.mozFullScreenElement && !doc.webkitFullscreenElement && !doc.msFullscreenElement) {
      if (requestFullScreen) {
        requestFullScreen.call(docEl).catch((err) => {
          console.error(`Error attempting to enable fullscreen mode: ${err.message} (${err.name})`);
        });
        setIsFullscreen(true);
      }
    } else {
      if (cancelFullScreen) {
        cancelFullScreen.call(doc);
        setIsFullscreen(false);
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFs = !!(document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement);
      setIsFullscreen(isFs);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isGridView) return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'Escape') {
        if (isGridView) setIsGridView(false);
      } else if (e.key === 'g') {
        setIsGridView(!isGridView);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, isGridView, nextSlide, prevSlide]);

  // Export Logic
  const handleExport = (scope, format) => {
    setExportConfig({ scope, format });
  };

  useEffect(() => {
    if (!exportConfig || !exportContainerRef.current) return;

    const performExport = async () => {
      // Wait for React to render the slides and images to load
      await new Promise(resolve => setTimeout(resolve, 2000));

      try {
        const elements = Array.from(exportContainerRef.current.children);
        console.log(`Exporting ${elements.length} slides...`);

        if (elements.length === 0) {
          throw new Error('No slides found in export container');
        }

        const { format } = exportConfig;

        if (format === 'pdf') {
          await exportToPdf(elements, 'zencap-pitch-deck.pdf');
        } else if (format === 'pptx') {
          await exportToPptx(elements, 'zencap-pitch-deck.pptx');
        } else if (format === 'zip') {
          await exportToZip(elements, 'zencap-pitch-deck-images.zip');
        } else if (format === 'png') {
          // Single slide export
          if (elements.length > 0) {
            await exportSingleToPng(elements[0], `zencap-slide-${currentSlide + 1}.png`);
          }
        }
        console.log('Export completed successfully!');
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please check console for details.");
      } finally {
        setExportConfig(null);
      }
    };

    performExport();
  }, [exportConfig, currentSlide]);

  const CurrentSlideComponent = slides[currentSlide].component;
  const progress = ((currentSlide + 1) / slides.length) * 100;

  // Touch/Swipe handling for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && !isGridView) {
      nextSlide();
    }
    if (isRightSwipe && !isGridView) {
      prevSlide();
    }
  };

  return (
    <TooltipProvider>
      <div
        className="min-h-[100svh] bg-background flex flex-col overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Header Navigation - Hidden in Fullscreen */}
        {!isFullscreen && (
          <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border transition-all duration-300 safe-area-top">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
              {/* Logo */}
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden flex items-center justify-center">
                  <img
                    src="/logo.png"
                    alt="ZenCap Logo"
                    className="w-full h-full object-cover scale-150 mix-blend-screen filter invert grayscale contrast-200"
                  />
                </div>
                <span className="font-semibold text-foreground text-sm sm:text-base hidden xs:inline">ZenCap Pitch Deck</span>
              </div>

              {/* Slide Counter */}
              <div className="flex items-center gap-2 sm:gap-4">
                <span className="text-muted-foreground text-xs sm:text-sm">
                  <span className="text-primary font-semibold">{currentSlide + 1}</span>/{slides.length}
                </span>

                {/* Progress Bar */}
                <div className="w-16 sm:w-32 hidden sm:block">
                  <Progress value={progress} className="h-1" />
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-1 sm:gap-2">
                <div className="hidden sm:block">
                  <ExportMenu onExport={handleExport} isExporting={!!exportConfig} />
                </div>

                <div className="w-px h-4 sm:h-6 bg-border mx-1 sm:mx-2 hidden sm:block" />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsGridView(!isGridView)}
                      className="text-muted-foreground hover:text-foreground w-8 h-8 sm:w-10 sm:h-10"
                    >
                      <Grid3X3 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Grid View (G)</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleFullscreen}
                      className="text-muted-foreground hover:text-foreground w-8 h-8 sm:w-10 sm:h-10"
                    >
                      {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Fullscreen</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </header>
        )}

        {/* Main Content */}
        <main className={`flex-1 flex flex-col items-center justify-center w-full h-full transition-all duration-300 ${isFullscreen ? 'p-0 bg-background' : 'pt-14 sm:pt-16 pb-16 sm:pb-20 px-2 sm:px-4'}`}>
          <AnimatePresence mode="wait">
            {isGridView ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8 w-full overflow-y-auto"
              >
                {/* Grid view header */}
                {!isFullscreen && <h2 className="text-lg sm:text-2xl font-semibold text-foreground mb-4 sm:mb-6">All Slides</h2>}

                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
                  {slides.map((slide, index) => {
                    const SlideComponent = slide.component;
                    return (
                      <motion.button
                        key={slide.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => goToSlide(index)}
                        className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all duration-300 hover:scale-105 ${currentSlide === index
                            ? 'border-primary shadow-glow'
                            : 'border-border hover:border-primary/50'
                          }`}
                      >
                        <div className="w-full h-full transform scale-[0.25] origin-top-left" style={{ width: '400%', height: '400%' }}>
                          <SlideComponent isPreview />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background to-transparent p-1 sm:p-2">
                          <span className="text-[10px] sm:text-xs text-muted-foreground">{slide.id}. {slide.title}</span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="w-full h-full flex items-center justify-center"
              >
                <ScalableSlide isFullscreen={isFullscreen}>
                  <CurrentSlideComponent />
                </ScalableSlide>
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        {/* Bottom Navigation - Hidden in Fullscreen */}
        {(!isGridView && !isFullscreen) && (
          <footer className="fixed bottom-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-t border-border safe-area-bottom">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
              {/* Previous */}
              <Button
                variant="ghost"
                onClick={prevSlide}
                disabled={currentSlide === 0}
                className="flex items-center gap-1 sm:gap-2 text-muted-foreground hover:text-foreground disabled:opacity-30 px-2 sm:px-4"
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Previous</span>
              </Button>

              {/* Slide Dots */}
              <div className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto max-w-[50vw] scrollbar-hide">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all duration-300 flex-shrink-0 ${currentSlide === index
                        ? 'bg-primary w-4 sm:w-6'
                        : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                      }`}
                  />
                ))}
              </div>

              {/* Next */}
              <Button
                variant="ghost"
                onClick={nextSlide}
                disabled={currentSlide === slides.length - 1}
                className="flex items-center gap-1 sm:gap-2 text-muted-foreground hover:text-foreground disabled:opacity-30 px-2 sm:px-4"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>
          </footer>
        )}

        {/* Floating controls for fullscreen mode (optional, but good UX) */}
        {isFullscreen && (
          <div className="fixed bottom-4 right-4 z-50 opacity-0 hover:opacity-100 transition-opacity">
            <Button
              variant="secondary"
              size="sm"
              onClick={toggleFullscreen}
              className="shadow-lg"
            >
              <Minimize2 className="w-4 h-4 mr-2" /> Exit Fullscreen
            </Button>
          </div>
        )}

        {/* Hidden Export Container - Must be on-screen for html-to-image to work */}
        {exportConfig && (
          <div
            ref={exportContainerRef}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: `${BASE_WIDTH}px`,
              height: `${BASE_HEIGHT}px`,
              pointerEvents: 'none',
              zIndex: 9999,
              opacity: 0.01, // Nearly invisible but still rendered
              overflow: 'hidden',
            }}
          >
            {(exportConfig.scope === 'all' ? slides : [slides[currentSlide]]).map(slide => {
              const Component = slide.component;
              return (
                <div
                  key={slide.id}
                  style={{
                    width: BASE_WIDTH,
                    height: BASE_HEIGHT,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                  className="bg-background"
                >
                  <Component isPreview={true} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default PitchDeck;